import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { usePlatformAuthStore } from './platformAuthStore.js';

let instance: AxiosInstance | undefined;
let refreshing: Promise<string> | null = null;

const apiBase = import.meta.env.VITE_API_BASE_URL || '/api/v1';
const platformBase = `${apiBase}/platform`;

/**
 * Axios client for /api/v1/platform/*. Lives separately from the
 * tenant client so the two auth pipelines (and their refresh cookies,
 * which are path-isolated) never cross.
 */
export function platformApi(): AxiosInstance {
  if (instance) return instance;
  instance = axios.create({
    baseURL: platformBase,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = usePlatformAuthStore.getState().accessToken;
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  instance.interceptors.response.use(
    (r) => r,
    async (err) => {
      const original = err.config as
        | (InternalAxiosRequestConfig & { _retried?: boolean })
        | undefined;
      const status = err.response?.status as number | undefined;
      if (status === 401 && original && !original._retried) {
        original._retried = true;
        try {
          const newAccess = await getRefresh();
          if (newAccess) {
            original.headers = original.headers ?? {};
            original.headers.Authorization = `Bearer ${newAccess}`;
            return instance!.request(original);
          }
        } catch {
          // fall through to logout
        }
        usePlatformAuthStore.getState().clear();
      }
      throw err;
    },
  );

  return instance;
}

async function getRefresh(): Promise<string> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const r = await axios.post<{ accessToken: string; expiresIn: number }>(
        `${platformBase}/auth/refresh`,
        {},
        { withCredentials: true },
      );
      usePlatformAuthStore.getState().setToken(r.data.accessToken, r.data.expiresIn);
      return r.data.accessToken;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}
